// =====================================================
// ACTIVIDAD GANADERA - TAB DEL PRODUCTOR
// src/pages/Agro/Productor/ActividadGanadera.js
// =====================================================
// MODIFICACIONES:
// - Se cargan valores de referencia de valor_ganaderia (getValoresGanaderia)
// - Nuevo resumen financiero por categoría con:
//   * Ingreso Bruto (BDSJEA-505: cabezas DE VENTA × peso_venta × precio_kilo,
//     bruto sin descontar gastos de venta; cierra con la card Valuación Total
//     y con la columna Valuación (USD) del stock)
//   * Costo Total (cabezas totales × (gastos_productivos + gastos_cria))
//   * Resultado Neto (ingreso bruto - costos - gastos_venta%)
//   * Los valores son de referencia pero editables por el cliente
// =====================================================

import React, { useState, useEffect, useMemo } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap"
import DataTable from "react-data-table-component"
import { opcionesPaginacion } from "components/Tables/paginacion"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts"
import classnames from "classnames"
import {
  formatearNumero,
  formatearFecha,
  formatearMonto,
  redondearArriba,
} from "helpers/formatters"

import {
  getStockPorProductor,
  getResumenGanadero,
  getTodosMovimientos,
  getEstablecimientos,
  getValoresGanaderia,
} from "api/agro"
import FormStock from "../StockGanadero/FormStock"
import FormMovimientoGanadero from "../StockGanadero/FormMovimientoGanadero"
import useAuthorization from "hooks/useAuthorization"

const formatRedondeadoArriba = value => formatearNumero(redondearArriba(value))

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
]

const renderPieLabel = ({ cx, cy, midAngle, outerRadius, name, percent }) => {
  const RADIAN = Math.PI / 180
  const radius = outerRadius + 26
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  return (
    <text
      x={x}
      y={y}
      fill="#495057"
      fontSize={12}
      fontWeight={500}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      <tspan x={x} dy={-6}>
        {name}
      </tspan>
      <tspan x={x} dy={14}>
        {`${(percent * 100).toFixed(0)}%`}
      </tspan>
    </text>
  )
}

const ActividadGanadera = ({ idProductor }) => {
  const { puedeModificarObjeto } = useAuthorization()
  const [loading, setLoading] = useState(true)
  const [stock, setStock] = useState([])
  const [resumen, setResumen] = useState(null)
  const [movimientos, setMovimientos] = useState([])
  const [establecimientos, setEstablecimientos] = useState([])
  const [valoresGanaderia, setValoresGanaderia] = useState([])
  const [activeTab, setActiveTab] = useState("resumen")
  const [filtroEst, setFiltroEst] = useState("")
  const [filtroEspecie, setFiltroEspecie] = useState("")
  const [filtroCategoria, setFiltroCategoria] = useState("")
  const [modalStock, setModalStock] = useState(false)
  const [modalMovimiento, setModalMovimiento] = useState(false)

  // Estado para overrides del cliente (edición de valores)
  const [overrides, setOverrides] = useState({})

  useEffect(() => {
    loadData()
  }, [idProductor])

  const loadData = async () => {
    setLoading(true)
    try {
      const [stockData, resumenData, estData, vgData] = await Promise.all([
        getStockPorProductor(idProductor),
        getResumenGanadero(idProductor),
        getEstablecimientos(idProductor),
        getValoresGanaderia().catch(() => []),
      ])
      setStock(stockData)
      setResumen(resumenData)
      setEstablecimientos(estData)
      setValoresGanaderia(vgData)
    } catch (error) {
      console.error("Error al cargar datos:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadMovimientos = async () => {
    try {
      setMovimientos([])
      const mov = await getTodosMovimientos(null, null, idProductor)
      setMovimientos(mov)
    } catch (error) {
      console.error("Error cargando movimientos:", error)
      setMovimientos([])
    }
  }

  useEffect(() => {
    if (activeTab === "movimientos") {
      loadMovimientos()
    }
  }, [activeTab])

  const movimientosFiltrados = useMemo(() => {
    let result = movimientos
    if (filtroEst)
      result = result.filter(
        m => Number(m.idEstablecimiento) === Number(filtroEst),
      )
    if (filtroEspecie) result = result.filter(m => m.especie === filtroEspecie)
    if (filtroCategoria)
      result = result.filter(m => m.categoria === filtroCategoria)
    return result
  }, [movimientos, filtroEst, filtroEspecie, filtroCategoria])

  const especiesOptions = useMemo(() => {
    const all = [...stock, ...movimientos]
    return [...new Set(all.map(r => r.especie).filter(Boolean))].sort()
  }, [stock, movimientos])

  const categoriasOptions = useMemo(() => {
    const base = filtroEspecie
      ? [...stock, ...movimientos].filter(r => r.especie === filtroEspecie)
      : [...stock, ...movimientos]
    return [...new Set(base.map(r => r.categoria).filter(Boolean))].sort()
  }, [stock, movimientos, filtroEspecie])

  // Datos para gráfico de distribución por categoría
  const chartDataCategorias = resumen?.detallePorCategoria
    ? Object.entries(resumen.detallePorCategoria).map(([cat, cant]) => ({
        name: cat,
        value: cant,
        // BDSJEA-505: cabezas de venta RESUELTAS por registro (venta si > 0, si no el
        // total), como las valúa el backend. Si el back no manda el campo nuevo, se cae
        // a la suma cruda de venta y, si es 0, al total de la categoría. OJO: ese
        // fallback con un back viejo opera POR CATEGORÍA, no por registro (no puede
        // ver cada registro), así que en categorías con registros mezclados (algunos
        // con venta y otros sin venta cargada) puede no cerrar con la valuación.
        valueVenta:
          resumen.detallePorCategoriaVentaResuelta?.[cat] ??
          (resumen.detallePorCategoriaVenta?.[cat] > 0
            ? resumen.detallePorCategoriaVenta[cat]
            : cant) ??
          0,
      }))
    : []

  // =====================================================
  // CÁLCULO DE RESUMEN FINANCIERO POR CATEGORÍA
  // Cruza stock con valores de referencia (valor_ganaderia)
  // =====================================================
  const resumenFinanciero = useMemo(() => {
    if (!chartDataCategorias.length || !resumen?.detallePorCategoria) return []

    // Crear mapa de valores ganadería por tipo de hacienda
    // valoresGanaderia viene como: { id, tipoHacienda/tipo_hacienda, pesoVenta/peso_venta,
    //   precioKilo/precio_kilo, gastosVenta/gastos_venta, gastosProductivos/gastos_productivos,
    //   gastosCria/gastos_cria }
    const vgMap = {}
    valoresGanaderia.forEach(vg => {
      const tipo = (vg.tipoHacienda || vg.tipo_hacienda || "").toLowerCase()
      if (tipo) vgMap[tipo] = vg
    })

    // Función para buscar valor ganadería por categoría
    const buscarValorGanaderia = categoria => {
      const catLower = (categoria || "").toLowerCase()
      // Buscar exacto
      if (vgMap[catLower]) return vgMap[catLower]
      // Buscar sin plural (Novillos → Novillo)
      const sinPlural = catLower.endsWith("s")
        ? catLower.slice(0, -1)
        : catLower
      if (vgMap[sinPlural]) return vgMap[sinPlural]
      // Buscar con plural
      if (vgMap[catLower + "s"]) return vgMap[catLower + "s"]
      // Buscar coincidencia parcial
      for (const [key, val] of Object.entries(vgMap)) {
        if (key.includes(catLower) || catLower.includes(key)) return val
      }
      return null
    }

    return chartDataCategorias.map((cat, idx) => {
      const key = `cat_${idx}`
      const cabezas = cat.value || 0
      const cabezasVenta = cat.valueVenta || 0
      const vg = buscarValorGanaderia(cat.name)

      // Valores de referencia
      const pesoVenta = vg ? parseFloat(vg.pesoVenta || vg.peso_venta) || 0 : 0
      const precioKilo = vg
        ? parseFloat(vg.precioKilo || vg.precio_kilo) || 0
        : 0
      const gastosVentaPct = vg
        ? parseFloat(vg.gastosVenta || vg.gastos_venta) || 0
        : 0
      const gastosProductivos = vg
        ? parseFloat(vg.gastosProductivos || vg.gastos_productivos) || 0
        : 0
      const gastosCria = vg
        ? parseFloat(vg.gastosCria || vg.gastos_cria) || 0
        : 0

      // Calcular (BDSJEA-505: ingreso y descuento de venta sobre las cabezas
      // DE VENTA, como la fórmula del back y la card; el costo total sigue
      // sobre las cabezas totales)
      const valorBruto = cabezasVenta * pesoVenta * precioKilo
      const costoProductivo = cabezas * (gastosProductivos + gastosCria)

      // Aplicar overrides del cliente si existen
      const ov = overrides[key] || {}
      const ingresoBruto =
        ov.ingresoBruto != null ? parseFloat(ov.ingresoBruto) : valorBruto
      const costoTotal =
        ov.costoTotal != null ? parseFloat(ov.costoTotal) : costoProductivo
      // El descuento de venta se calcula sobre el bruto EFECTIVO (con override si lo
      // hubiera; hoy setOverrides solo se llama con {} y no hay edición de celdas)
      const descuentoVenta = ingresoBruto * (gastosVentaPct / 100)
      const resultadoNeto = ingresoBruto - costoTotal - descuentoVenta

      return {
        key,
        categoria: cat.name,
        cabezas,
        cabezasVenta,
        pesoVenta,
        precioKilo,
        gastosVentaPct,
        gastosProductivos,
        gastosCria,
        ingresoBruto,
        costoTotal,
        resultadoNeto,
        tieneValoresRef: !!vg,
      }
    })
  }, [chartDataCategorias, valoresGanaderia, overrides, resumen])

  // Totales financieros
  const totalesFinancieros = useMemo(() => {
    return resumenFinanciero.reduce(
      (acc, r) => ({
        cabezas: acc.cabezas + r.cabezas,
        cabezasVenta: acc.cabezasVenta + r.cabezasVenta,
        ingresoBruto: acc.ingresoBruto + r.ingresoBruto,
        costoTotal: acc.costoTotal + r.costoTotal,
        resultadoNeto: acc.resultadoNeto + r.resultadoNeto,
      }),
      {
        cabezas: 0,
        cabezasVenta: 0,
        ingresoBruto: 0,
        costoTotal: 0,
        resultadoNeto: 0,
      },
    )
  }, [resumenFinanciero])

  // Columnas para tabla de stock
  const columnsStock = [
    {
      name: "Establecimiento",
      selector: row => row.nombreEstablecimiento,
      sortable: true,
      grow: 1,
      minWidth: "140px",
    },
    {
      name: "Especie",
      selector: row => row.especie,
      sortable: true,
      grow: 0,
      minWidth: "120px",
    },
    {
      name: "Categoría",
      selector: row => row.categoria,
      sortable: true,
      grow: 2,
      minWidth: "160px",
    },
    {
      name: (
        <span>
          Cabezas
          <br />
          Totales
        </span>
      ),
      selector: row => row.cantidadCabezas,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "110px",
      format: row => formatearNumero(row.cantidadCabezas) || "-",
    },
    {
      name: (
        <span>
          Cabezas
          <br />
          Para Venta
        </span>
      ),
      selector: row => row.cantidadCabezasVenta,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "120px",
      format: row => formatearNumero(row.cantidadCabezasVenta) || "-",
    },
    {
      name: (
        <>
          Peso Promedio
          <br />
          (kg)
        </>
      ),
      selector: row => row.pesoPromedioKg,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "110px",
      format: row => formatearNumero(row.pesoPromedioKg) || "-",
    },
    {
      name: (
        <>
          Valuación
          <br />
          (USD)
        </>
      ),
      selector: row => row.valuacionTotalUsd,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "130px",
      format: row =>
        row.valuacionTotalUsd && row.valuacionTotalUsd > 0
          ? formatearNumero(row.valuacionTotalUsd, 2)
          : "-",
    },
    {
      id: "fechaStock",
      name: (
        <>
          Fecha
          <br />
          Stock
        </>
      ),
      selector: row => row.fechaStock,
      sortable: true,
      grow: 0,
      minWidth: "150px",
      format: row => formatearFecha(row.fechaStock),
    },
  ]

  // Columnas para tabla de movimientos
  const columnsMovimientos = [
    {
      id: "fechaMovimiento",
      name: "Fecha",
      selector: row => row.fechaMovimiento,
      sortable: true,
      grow: 0,
      minWidth: "120px",
      format: row => formatearFecha(row.fechaMovimiento),
    },
    {
      name: "Establecimiento",
      selector: row => row.nombreEstablecimiento,
      sortable: true,
      grow: 1,
      minWidth: "140px",
    },
    {
      name: "Tipo",
      selector: row => row.tipoMovimiento,
      sortable: true,
      grow: 0,
      minWidth: "90px",
      cell: row => (
        <Badge color={row.tipoMovimiento === "INGRESO" ? "success" : "danger"}>
          {row.tipoMovimiento}
        </Badge>
      ),
    },
    {
      name: "Motivo",
      selector: row => row.motivo,
      sortable: true,
      grow: 0,
      minWidth: "110px",
    },
    {
      name: "Especie",
      selector: row => row.especie,
      sortable: true,
      grow: 0,
      minWidth: "120px",
    },
    {
      name: "Categoría",
      selector: row => row.categoria,
      sortable: true,
      grow: 2,
      minWidth: "160px",
    },
    {
      name: (
        <span>
          Cabezas
          <br />
          Totales
        </span>
      ),
      selector: row => row.cantidadCabezas,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "100px",
      format: row => formatearNumero(row.cantidadCabezas),
    },
    {
      name: (
        <span>
          Cabezas
          <br />
          Venta
        </span>
      ),
      selector: row => row.cantidadCabezasVenta,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "100px",
      format: row => formatearNumero(row.cantidadCabezasVenta),
    },
    {
      name: (
        <>
          Peso Total
          <br />
          (kg)
        </>
      ),
      selector: row => row.pesoTotalKg,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "100px",
      format: row => formatearNumero(row.pesoTotalKg),
    },
    {
      name: (
        <>
          Importe
          <br />
          (USD/ARS)
        </>
      ),
      selector: row => row.importeTotal,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "110px",
      format: row => formatearNumero(row.importeTotal),
    },
    {
      name: "DTE/Guía",
      selector: row => row.numeroDte,
      grow: 0,
      minWidth: "110px",
    },
  ]

  // Filtrar stock por establecimiento, especie y categoría
  const stockFiltrado = stock
    .filter(s => !filtroEst || s.idEstablecimiento === parseInt(filtroEst))
    .filter(s => !filtroEspecie || s.especie === filtroEspecie)
    .filter(s => !filtroCategoria || s.categoria === filtroCategoria)

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p className="mt-3 text-muted">Cargando actividad ganadera...</p>
      </div>
    )
  }

  return (
    <>
      <style>{`
        .rdt_TableHead .rdt_TableHeadCell {
          white-space: normal !important;
          text-align: center !important;
          vertical-align: middle !important;
          line-height: 1.2 !important;
        }
        .tabla-stock .rdt_TableHead .rdt_TableHeadCell,
        .tabla-movimientos .rdt_TableHead .rdt_TableHeadCell {
          justify-content: center !important;
        }
      `}</style>
      {/* Resumen General - KPIs */}
      <Row className="mb-3">
        <Col md={6}>
          <Card className="bg-success text-white">
            <CardBody className="text-center">
              <h3 className="mb-0" data-testid="kpi-valuacion-total">
                {formatearMonto(resumen?.valuacionTotalUsd || 0, "USD", 0)}
              </h3>
              <small>
                Valuación Total
                <i
                  className="mdi mdi-information-outline ms-1 text-white-50"
                  style={{ fontSize: 12, cursor: "help" }}
                  title="Suma por registro de (cabezas para venta × peso promedio × precio de referencia USD/kg). Puede diferir del Ingreso Bruto del Resumen por Categoría (peso y precio de Valor Ganadería) cuando el registro tiene peso/precio propios"
                />
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={6}>
          <Card
            className={`text-white ${totalesFinancieros.resultadoNeto < 0 ? "bg-danger" : "bg-info"}`}
          >
            <CardBody className="text-center">
              <h3 className="mb-0" data-testid="kpi-resultado-neto">
                {formatearMonto(
                  redondearArriba(totalesFinancieros.resultadoNeto),
                  "USD",
                  0,
                )}
              </h3>
              <small>
                Resultado Neto (USD)
                <i
                  className="mdi mdi-information-outline ms-1 text-white-50"
                  style={{ fontSize: 12, cursor: "help" }}
                  title="Ingreso Bruto − Costo Total − Gastos de venta % (Resumen por Categoría)"
                />
              </small>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Gráfico + Resumen Financiero */}
      {chartDataCategorias.length > 0 && (
        <Row className="mb-3">
          <Col md={5}>
            <Card className="h-100">
              <CardBody>
                <h6 className="card-title">Distribución por Categoría</h6>
                <ResponsiveContainer width="100%" height={380}>
                  <PieChart>
                    <Pie
                      data={chartDataCategorias}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={105}
                      paddingAngle={2}
                      minAngle={4}
                      label={renderPieLabel}
                      labelLine={{ strokeWidth: 1, stroke: "#666" }}
                    >
                      {chartDataCategorias.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={value => `${value.toLocaleString()} cab.`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>
          </Col>

          {/* =====================================================
              NUEVO: RESUMEN FINANCIERO POR CATEGORÍA
              ===================================================== */}
          <Col md={7}>
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="card-title mb-0">Resumen por Categoría</h6>
                  {valoresGanaderia.length === 0 && (
                    <Badge color="warning" pill>
                      <i className="mdi mdi-alert me-1" />
                      Sin valores de referencia
                    </Badge>
                  )}
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th className="text-center">Categoría</th>
                        <th className="text-center">
                          Totales
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Cantidad total de cabezas declaradas en stock para esta categoría"
                          />
                        </th>
                        <th className="text-center">
                          Para la venta
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Cabezas resueltas para la venta: por cada registro se toma la venta cargada y, si no hay venta cargada (vacía o 0), se incluye el total de cabezas del registro. Por eso puede superar la suma de las cabezas de venta cargadas"
                          />
                        </th>
                        <th className="text-center">
                          Ingreso Bruto (USD)
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Cabezas para la venta × Peso de venta (kg) × Precio por kilo (Valor Ganadería). Puede diferir de la valuación del stock cuando el registro tiene peso/precio propios"
                          />
                        </th>
                        <th className="text-center">
                          Costo Total (USD)
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Cabezas totales × (Gastos productivos + Gastos de cría)"
                          />
                        </th>
                        <th className="text-center">
                          Resultado Neto (USD)
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Ingreso Bruto − Costo Total − Gastos de venta %"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumenFinanciero.map((r, idx) => (
                        <tr key={idx}>
                          <td>
                            <span
                              className="me-2"
                              style={{
                                display: "inline-block",
                                width: 12,
                                height: 12,
                                backgroundColor: COLORS[idx % COLORS.length],
                                borderRadius: 2,
                              }}
                            />
                            {r.categoria}
                            {!r.tieneValoresRef && (
                              <i
                                className="mdi mdi-alert-circle text-warning ms-1"
                                style={{ fontSize: 12, cursor: "help" }}
                                title="No hay valores de referencia (peso, precio, gastos) para esta categoría en la tabla de Valor Ganadería. Los campos de Ingreso Bruto, Costo Total y Resultado Neto no pueden calcularse."
                              />
                            )}
                          </td>
                          <td className="text-end">
                            {formatearNumero(r.cabezas)}
                          </td>
                          <td className="text-end">
                            {formatearNumero(r.cabezasVenta)}
                          </td>
                          <td className="text-end text-success">
                            {formatRedondeadoArriba(r.ingresoBruto)}
                          </td>
                          <td className="text-end text-danger">
                            {formatRedondeadoArriba(r.costoTotal)}
                          </td>
                          <td
                            className={`text-end fw-bold ${r.resultadoNeto >= 0 ? "text-success" : "text-danger"}`}
                          >
                            {formatRedondeadoArriba(r.resultadoNeto)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td>Total</td>
                        <td className="text-end">
                          {formatearNumero(totalesFinancieros.cabezas)}
                        </td>
                        <td
                          className="text-end"
                          title="Suma de las cabezas resueltas para la venta de todas las categorías (incluye el total del registro cuando no hay venta cargada)"
                        >
                          {formatearNumero(totalesFinancieros.cabezasVenta)}
                        </td>
                        <td className="text-end text-success">
                          {formatRedondeadoArriba(
                            totalesFinancieros.ingresoBruto,
                          )}
                        </td>
                        <td className="text-end text-danger">
                          {formatRedondeadoArriba(
                            totalesFinancieros.costoTotal,
                          )}
                        </td>
                        <td
                          className={`text-end ${totalesFinancieros.resultadoNeto >= 0 ? "text-success" : "text-danger"}`}
                        >
                          {formatRedondeadoArriba(
                            totalesFinancieros.resultadoNeto,
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {Object.keys(overrides).length > 0 && (
                    <div className="text-end mt-1">
                      <small className="text-muted me-2">
                        <i className="mdi mdi-pencil me-1" />
                        Valores editados por el usuario
                      </small>
                      <Button
                        size="sm"
                        color="link"
                        className="p-0 text-muted"
                        onClick={() => setOverrides({})}
                      >
                        Restaurar originales
                      </Button>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabs y Tabla */}
      <style>
        {`
          .nav-tabs-agro .nav-link.active {
            color: #495057 !important;
            background-color: #fff !important;
            font-weight: 600 !important;
            border-color: #dee2e6 #dee2e6 #fff !important;
          }
          .nav-tabs-agro .nav-link {
            color: #6c757d !important;
            cursor: pointer;
          }
          .nav-tabs-agro .nav-link:hover:not(.active) {
            color: #495057 !important;
            border-color: #e9ecef #e9ecef #dee2e6 !important;
          }
        `}
      </style>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Nav tabs className="nav-tabs-agro">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "resumen" })}
                  onClick={() => setActiveTab("resumen")}
                  style={{ cursor: "pointer" }}
                >
                  Stock Actual
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === "movimientos",
                  })}
                  onClick={() => setActiveTab("movimientos")}
                  style={{ cursor: "pointer" }}
                >
                  Movimientos
                </NavLink>
              </NavItem>
            </Nav>
            {activeTab === "resumen" &&
              puedeModificarObjeto("/agro/establecimientos") && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setModalStock(true)}
                >
                  <i className="mdi mdi-plus me-1" />
                  Registrar Stock
                </Button>
              )}
            {activeTab !== "resumen" &&
              puedeModificarObjeto("/agro/establecimientos") && (
                <Button
                  color="success"
                  onClick={() => setModalMovimiento(true)}
                >
                  <i className="mdi mdi-plus me-1" />
                  Nuevo Movimiento
                </Button>
              )}
          </div>

          {/* Filtros */}
          <Row className="mb-3 g-2">
            <Col md={4}>
              <Input
                type="select"
                bsSize="sm"
                value={filtroEst}
                onChange={e => {
                  setFiltroEst(e.target.value)
                  if (e.target.value && activeTab === "movimientos") {
                    loadMovimientos(e.target.value)
                  }
                }}
              >
                <option value="">Todos los establecimientos</option>
                {establecimientos.map(est => (
                  <option key={est.id} value={est.id}>
                    {est.nombre}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Input
                type="select"
                bsSize="sm"
                value={filtroEspecie}
                onChange={e => {
                  setFiltroEspecie(e.target.value)
                  setFiltroCategoria("")
                }}
              >
                <option value="">Todas las especies</option>
                {especiesOptions.map(esp => (
                  <option key={esp} value={esp}>
                    {esp}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Input
                type="select"
                bsSize="sm"
                value={filtroCategoria}
                onChange={e => setFiltroCategoria(e.target.value)}
              >
                <option value="">Todas las categorías</option>
                {categoriasOptions.map(cat => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          {activeTab === "resumen" ? (
            <DataTable
              className="tabla-stock"
              columns={columnsStock}
              data={stockFiltrado}
              pagination
              paginationPerPage={10}
              {...opcionesPaginacion(stockFiltrado.length)}
              highlightOnHover
              defaultSortFieldId="fechaStock"
              defaultSortAsc={false}
              noDataComponent={
                <div className="text-center py-4">
                  <i
                    className="mdi mdi-cow text-muted"
                    style={{ fontSize: 48 }}
                  />
                  <p className="text-muted mt-2">
                    No hay stock ganadero registrado
                  </p>
                </div>
              }
            />
          ) : (
            <>
              <DataTable
                className="tabla-movimientos"
                columns={columnsMovimientos}
                data={movimientosFiltrados}
                pagination
                paginationPerPage={10}
                {...opcionesPaginacion(movimientosFiltrados.length)}
                highlightOnHover
                fixedHeader
                defaultSortFieldId="fechaMovimiento"
                defaultSortAsc={false}
                noDataComponent={
                  <div className="text-center py-4">
                    <p className="text-muted">No hay movimientos registrados</p>
                  </div>
                }
              />
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal Stock */}
      <Modal isOpen={modalStock} toggle={() => setModalStock(false)} size="xl">
        <ModalHeader toggle={() => setModalStock(false)}>
          Registrar Stock Ganadero
        </ModalHeader>
        <ModalBody>
          <FormStock
            idProductor={idProductor}
            establecimientos={establecimientos}
            idEstablecimiento={filtroEst}
            onSuccess={() => {
              setModalStock(false)
              loadData()
            }}
            onCancel={() => setModalStock(false)}
          />
        </ModalBody>
      </Modal>

      {/* Modal Movimiento */}
      <Modal
        isOpen={modalMovimiento}
        toggle={() => setModalMovimiento(false)}
        size="xl"
      >
        <ModalHeader toggle={() => setModalMovimiento(false)}>
          Registrar Movimiento Ganadero
        </ModalHeader>
        <ModalBody>
          <FormMovimientoGanadero
            idEstablecimiento={filtroEst}
            establecimientos={establecimientos}
            onSuccess={() => {
              setModalMovimiento(false)
              loadMovimientos(filtroEst)
              loadData()
            }}
            onCancel={() => setModalMovimiento(false)}
          />
        </ModalBody>
      </Modal>
    </>
  )
}

export default ActividadGanadera
